import { useMutation, useQuery } from "@tanstack/react-query";

import { PROGRAM_STATUS } from "constants/batchPreference";
import { PLUTUS_SOURCE_ID } from "constants/config";
import { getAuthToken } from "utils/tokenUtils";
import {
  buildPreferenceSuggestionsPayload,
  buildProcessPreferencesRequestPayload,
  buildSuggestedProgramsPayload,
  normalizePreferenceSuggestions,
  normalizeSuggestedPrograms,
} from "utils/batchPreferenceUtils";

const getOneAppApiBaseUrl = () =>
  process.env.REACT_APP_ONE_APP_API_URL || "https://oneapp.devinfinitylearn.in";

const PROCESS_ENROLLMENT_OK_CODES = new Set([
  "",
  "BATCH_ENROLLMENT_SUCCESS",
  "BATCH_ENROLLMENT_IN_PROGRESS",
]);

const adminHeaders = () => ({
  accept: "application/json",
  "Content-Type": "application/json",
  "auth-token": getAuthToken(),
  Authorization: `Bearer ${getAuthToken()}`,
});

const oneOmsHeaders = () => {
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
    "auth-token": getAuthToken(),
    "x-source-id": PLUTUS_SOURCE_ID,
  };
  const omsSecret = process.env.REACT_APP_ONE_OMS_SECRET_KEY;
  if (omsSecret) {
    headers["secret-key"] = omsSecret;
  }
  return headers;
};

const extractErrorMessage = (data, fallback) =>
  data?.error?.message || data?.message || data?.detail || fallback;

const extractProcessErrorMessage = (data, fallback) =>
  data?.error?.message ||
  data?.enrollmentStatus?.message ||
  data?.message ||
  fallback;

const withPendingStatus = (programs) =>
  programs.map((program) => ({
    ...program,
    status: PROGRAM_STATUS.PENDING,
    decision: null,
  }));

const fetchSuggestedPrograms = async ({
  orderId,
  order,
  userId,
  gatewayTenantId,
  gatewayBranchId,
}) => {
  const baseUrl = getOneAppApiBaseUrl();
  if (!baseUrl) {
    throw new Error("OneApp API is not configured.");
  }

  const suggestedPayload = buildSuggestedProgramsPayload({
    orderId,
    userId,
    order,
    gatewayTenantId,
    gatewayBranchId,
  });

  const response = await fetch(
    `${baseUrl}/admin/get/smartbatching/suggested-programs`,
    {
      method: "POST",
      headers: adminHeaders(),
      body: JSON.stringify(suggestedPayload),
    },
  );
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(
      extractErrorMessage(data, "Unable to fetch suggested programs."),
    );
  }

  return withPendingStatus(normalizeSuggestedPrograms(data));
};

export const useSuggestedPrograms = ({
  orderId,
  order,
  userId,
  gatewayTenantId,
  gatewayBranchId,
  enabled,
}) => {
  return useQuery({
    queryKey: [
      "batch-preference-suggested-programs",
      orderId,
      userId,
      gatewayTenantId,
      gatewayBranchId,
    ],
    queryFn: () =>
      fetchSuggestedPrograms({
        orderId,
        order,
        userId,
        gatewayTenantId,
        gatewayBranchId,
      }),
    enabled: Boolean(enabled && orderId && order && userId && gatewayTenantId),
  });
};

const fetchPreferenceSuggestions = async ({
  programId,
  order,
  userId,
  gatewayTenantId,
  gatewayBranchId,
}) => {
  const baseUrl = getOneAppApiBaseUrl();
  if (!baseUrl) {
    throw new Error("OneApp API is not configured.");
  }
  if (!programId) {
    throw new Error("Program is required to load preference options.");
  }

  const response = await fetch(
    `${baseUrl}/admin/get/smartbatching/programs/${programId}/preferences/suggestions`,
    {
      method: "POST",
      headers: adminHeaders(),
      body: JSON.stringify(
        buildPreferenceSuggestionsPayload({
          userId,
          order,
          gatewayTenantId,
          gatewayBranchId,
        }),
      ),
    },
  );
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(
      extractErrorMessage(data, "Unable to fetch preference options."),
    );
  }

  return normalizePreferenceSuggestions(data);
};

export const usePreferenceSuggestions = ({
  programId,
  order,
  userId,
  gatewayTenantId,
  gatewayBranchId,
  enabled,
}) => {
  return useQuery({
    queryKey: [
      "batch-preference-suggestions",
      programId,
      userId,
      gatewayTenantId,
      gatewayBranchId,
    ],
    queryFn: () =>
      fetchPreferenceSuggestions({
        programId,
        order,
        userId,
        gatewayTenantId,
        gatewayBranchId,
      }),
    enabled: Boolean(
      enabled && programId && order && userId && gatewayTenantId,
    ),
  });
};

const processPreference = async ({
  programId,
  decision,
  order,
  userId,
  gatewayTenantId,
  gatewayBranchId,
}) => {
  const baseUrl = getOneAppApiBaseUrl();
  if (!baseUrl) {
    throw new Error("OneApp API is not configured.");
  }

  const response = await fetch(
    `${baseUrl}/admin/smartbatching/programs/${programId}/preferences/process`,
    {
      method: "POST",
      headers: adminHeaders(),
      body: JSON.stringify(
        buildProcessPreferencesRequestPayload({
          decision,
          userId,
          order,
          gatewayTenantId,
          gatewayBranchId,
        }),
      ),
    },
  );
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(
      extractProcessErrorMessage(data, "Unable to save batch preference."),
    );
  }

  const enrollmentCode = String(data?.enrollmentStatus?.code || "").trim();
  if (enrollmentCode && !PROCESS_ENROLLMENT_OK_CODES.has(enrollmentCode)) {
    throw new Error(
      extractProcessErrorMessage(data, "Unable to save batch preference."),
    );
  }

  return data;
};

export const useProcessPreference = () => {
  return useMutation({ mutationFn: processPreference });
};

const skipBatching = async ({
  orderId,
  programId,
  skipReasonCode,
  skipReasonDetail,
}) => {
  const baseUrl = process.env.REACT_APP_ONE_OMS_URL;
  if (!baseUrl) {
    throw new Error("One OMS is not configured.");
  }
  if (!orderId || !programId || !skipReasonCode) {
    throw new Error("Order, program, and skip reason are required.");
  }

  const payload = {
    order_id: orderId,
    program_id: programId,
    skip_reason_code: skipReasonCode,
  };
  if (skipReasonDetail) {
    payload.skip_reason_detail = skipReasonDetail;
  }

  const response = await fetch(`${baseUrl}/skip-batching`, {
    method: "POST",
    headers: oneOmsHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(
      extractErrorMessage(data, "Unable to save the skip reason."),
    );
  }

  return data;
};

export const useSkipBatching = () => {
  return useMutation({ mutationFn: skipBatching });
};
